import axios from "axios";
import { baseURL } from "../Constants";
import { Account } from "../models/Account";
import { PlayerTeamHistory } from "../models/PlayerTeamHistory";
import { LFT } from "../models/LFT";
import { PlayerStats } from "../models/PlayerStats";
import { Team } from "../models/Team";

const url = `${baseURL}/Account`;

export interface AdminUser {
    steamId: string;
    playerName: string | null;
    donator: boolean;
    beta: boolean;
    premium: boolean;
    admin: boolean;
}

// Admin-only: every account with its donator/beta/premium flags (JWT must belong to an admin).
export const getAdminUsers = async (): Promise<AdminUser[]> => {
    const token = localStorage.getItem("token");
    const response = await axios.get<AdminUser[]>(`${url}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
};

const roundPlayerStats = (player: PlayerStats): PlayerStats => {
    const roundedPlayer: PlayerStats = {
        ...player,
        kills: Math.round(player.kills * 100) / 100,
        hsKills: Math.round(player.hsKills * 100) / 100,
        deaths: Math.round(player.deaths * 100) / 100,
        assists: Math.round(player.assists * 100) / 100,
        hs: Math.round(player.hs * 100) / 100,
        dmg: Math.round(player.dmg * 100) / 100,
        adr: Math.round(player.adr * 100) / 100,
        kd: Math.round(player.kd * 100) / 100,
        fk: Math.round(player.fk * 100) / 100,
        kast: Math.round(player.kast * 100) / 100,
        rating1: Math.round(player.rating1 * 100) / 100,
        rating2: Math.round(player.rating2 * 100) / 100,
        openingDuelWinPercentage: Math.round(player.openingDuelWinPercentage * 100) / 100,
        openingDuelAttemptPercentage: Math.round(player.openingDuelAttemptPercentage * 100) / 100,
        enemyFlashTime: Math.round(player.enemyFlashTime * 100) / 100,
        timeFlashed: Math.round(player.timeFlashed * 100) / 100,
        utilityDamage: Math.round(player.utilityDamage * 100) / 100,
        gamesPlayed: Math.round(player.gamesPlayed * 100) / 100,
    };
    return roundedPlayer;
}


export const fetchAccount = async (steamId: string): Promise<Account | undefined> => {
    try {
        const response = await axios.get(`${url}/${steamId}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const fetchPlayerTeamHistory = async (steamId: string): Promise<PlayerTeamHistory[]> => {
    try {
        const response = await axios.get(`${url}/getPlayerTeamHistory?steamId=${steamId}`);
        const playerTeamHistory = response.data as PlayerTeamHistory[];
        playerTeamHistory.forEach(player => {
            player.rating = player.rating !== -1 ? Math.round(player.rating * 100) / 100 : player.rating;
        });
        return playerTeamHistory;
    }
    catch (error) {
        throw error;
    }
}

export const updateAccount = async (playerData: Account, token: string): Promise<boolean> => {
    try {
        const response = await axios.post(`${url}/update`, playerData, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const setBetaStatus = async (steamId: string, beta: boolean, token: string): Promise<boolean> => {
    try {
        const response = await axios.post(`${url}/setBeta`, { steamId, beta }, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data.success;
    } catch (error) {
        throw error;
    }
}

export const fetchLFTPlayers = async (): Promise<LFT[]> => {
    try {
        const response = await axios.get(`${url}/lft`);
        let players = response.data as LFT[];
        players.forEach(player => {
            if (player.stats)
                player.stats = roundPlayerStats(player.stats);
        });
        return players;

    } catch (error) {
        throw error;
    }
};

export interface SetLFTResponse {
    success: boolean;
    lft: boolean;
    lftDate?: string;
    role?: string;
}

// Self-service "sæt dig som ledig" toggle from the LFT page. Backend refreshes the
// LFT cache before responding, so a refetch of /lft immediately reflects the change.
export const setLFTStatus = async (lft: boolean, role: string | undefined, token: string): Promise<SetLFTResponse> => {
    const response = await axios.post(`${url}/lft`, { lft, role }, {
        headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
};

export const fetchPlayersByPlayerName = async (name: string): Promise<Account[]> => {
    try {
        const response = await axios.get(`${url}/playersByPlayerName?username=${name}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}
export const fetchPlayersByPlayerNameAndSeason = async (name: string, season: number): Promise<Account[]> => {
    try {
        const response = await axios.get(`${url}/playersByPlayerNameAndSeason?username=${name}&season=${season}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const postAccount = async (account: Account) => {
    try {
        const response = await axios.post(`${url}`, account);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const fetchCurrentTeam = async (steamId: string): Promise<Team | undefined> => {
    try {
        const response = await axios.get(`${url}/getCurrentTeam?steamid=${steamId}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export interface CalendarUrlResponse {
    url: string;
    token: string;
}

const calendarUrl = `${baseURL}/Calendar`;

export const fetchCalendarUrl = async (token: string): Promise<CalendarUrlResponse> => {
    const response = await axios.get(`${calendarUrl}/url`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
}

export const rotateCalendarToken = async (token: string): Promise<CalendarUrlResponse> => {
    const response = await axios.post(`${calendarUrl}/rotate`, null, {
        headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
}